<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="$t('coop_business_point_applications')"
                        :isFilter="true"
                        @filter-div-status="datatable.filterStatus = $event" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="$t('coop_business_point_applications')"
                              :isFilter="true"
                              @filter-div-status="datatable.filterStatus = $event" />
            </template>

            <!-- Filter -->
            <datatable-filter v-show="datatable.filterStatus"
                               @filter="filter"
                               @filterClear="filterClear"
                               :export-excel="checkPermission('coopbusinesspointapplication_excelexport')"
                               @exportExcel="exportExcel()">
                <b-row>
                    <b-col sm="6" md="4">
                        <b-form-group :label="$t('student_number')">
                            <b-form-input v-model="datatable.queryParams.filter.student_number" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </datatable-filter>

            <!-- Datatable -->
            <datatable ref="datatable"
                       :isLoading.sync="datatable.isLoading"
                       :columns="datatable.columns"
                       :rows="datatable.rows"
                       :total="datatable.total"
                       :queryParams="datatable.queryParams"
                       :lineNumbers="false"
                       @on-page-change="onPageChange"
                       @on-sort-change="onSortChange"
                       @on-per-page-change="onPerPageChange"
                       v-show="datatable.showTable"
            />
        </app-layout>
    </div>
</template>

<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import DatatableFilter from '@/components/datatable/DatatableFilter';
    import Datatable from '@/components/datatable/Datatable';

    //Service
    import CoopBusinessPointApplicationService from '@/services/CoopBusinessPointApplicationService';

    //Other
    import qs from 'qs';

    export default {
        name: 'BusinessPointApplicationsParticipants',
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            DatatableFilter,
            Datatable
        },
        metaInfo() {
            return {
                title: this.$t('coop_business_point_applications')
            };
        },
        data() {
            return {
                datatable: {
                    isLoading: false,
                    filterStatus: true,
                    columns: [
                        {
                            label: '',
                            field: 'buttons',
                            html: true,
                            hidden: false,
                            tdClass: 'p-0 text-center w-40 align-middle',
                            buttons: [
                                {
                                    text: this.$t('coop_view_cv'),
                                    class: 'ri-file-user-line text-primary',
                                    permission: 'coopbusinesspointapplication_showcv',
                                    callback: (row) => {
                                        this.downloadCv(row);
                                    }
                                },
                                {
                                    text: this.$t('delete'),
                                    class: 'ri-delete-bin-line text-danger',
                                    permission: 'coopbusinesspointapplication_delete',
                                    callback: (row) => {
                                        this.deleteApplication(row);
                                    }
                                }
                            ]
                        },
                        {
                            label: this.toUpperCase('student_number'),
                            field: 'student_number',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('name'),
                            field: 'name',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('surname'),
                            field: 'surname',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('coop_start_time'),
                            field: 'slot_start_time',
                            hidden: false
                        },
                        {
                            label: this.toUpperCase('date'),
                            field: 'created_at',
                            hidden: false
                        }
                    ],
                    rows: [],
                    total: 0,
                    showTable: false,
                    queryParams: {
                        filter: {},
                        sort: '-id',
                        page: 1,
                        limit: 20
                    }
                }
            };
        },
        created() {
            if (this.$route.params.id) {
                this.datatable.queryParams.filter.event_id = this.$route.params.id;
            }
            this.getRows();
        },
        methods: {
            getRows() {
                if (!this.checkPermission('coopbusinesspointapplication_index')) {
                    return;
                }
                this.datatable.isLoading = true;
                if (this.$route.params.id) {
                    this.datatable.queryParams.filter.event_id = this.$route.params.id;
                }
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                CoopBusinessPointApplicationService.getAll(config)
                    .then((response) => {
                        this.datatable.rows = response.data.data;
                        this.datatable.total = response.data.pagination?.total || response.data.data.length;
                        this.datatable.showTable = true;
                    })
                    .catch((error) => {
                        this.showErrors(error, null);
                    })
                    .finally(() => {
                        this.datatable.isLoading = false;
                    });
            },
            exportExcel() {
                if (this.$route.params.id) {
                    this.datatable.queryParams.filter.event_id = this.$route.params.id;
                }
                const config = {
                    params: { ...this.datatable.queryParams },
                    paramsSerializer: (params) => qs.stringify(params, { encode: false })
                };

                return CoopBusinessPointApplicationService.excelExport(config)
                    .then((response) => {
                        this._downloadFile(response, 'business_point_applications.xlsx');
                    })
                    .catch((e) => {
                        this.showErrors(e, null);
                    });
            },
            downloadCv(row) {
                CoopBusinessPointApplicationService.showCv(row.id)
                    .then((response) => {
                        this._downloadFile(response, `${row.student_number || 'student'}_cv.pdf`);
                    })
                    .catch((e) => {
                        this.showErrors(e, null);
                    });
            },
            deleteApplication(row) {
                this.$swal.fire({
                    text: this.$t('coop_business_point_application_delete_confirm'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no')
                }).then((result) => {
                    if (result.isConfirmed) {
                        CoopBusinessPointApplicationService.remove(row.id)
                            .then((response) => {
                                this.$toast.success(this.$t('api.' + response.data.message));
                                this.getRows();
                            })
                            .catch((error) => {
                                this.showErrors(error, null);
                            });
                    }
                });
            },
            filter() {
                this.datatable.queryParams.page = 1;
                this.getRows();
            },
            filterClear() {
                this.datatable.queryParams.filter = {};
                if (this.$route.params.id) {
                    this.datatable.queryParams.filter.event_id = this.$route.params.id;
                }
                this.filter();
            },
            onPageChange(params) {
                this.datatable.queryParams.page = params.currentPage;
                this.getRows();
            },
            onPerPageChange(params) {
                this.datatable.queryParams.limit = params.currentPerPage;
                this.getRows();
            },
            onSortChange(params) {
                if (params && params.length > 0) {
                    this.datatable.queryParams.sort = (params[0].type === 'desc' ? '-' : '') + params[0].field;
                }
                this.getRows();
            }
        }
    };
</script>
